import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from '../hooks/index.ts';
import { Field, classes } from './ui.tsx';

/**
 * One box that is both the search and the choice.
 *
 * It replaces a pattern this app had grown five copies of: a "Find a client"
 * text box sitting next to a "Client" dropdown, where typing in the first one
 * silently reloads the second. Nothing on screen said the two were connected,
 * and the arrangement read as two questions when it was one — people filled in
 * the search box, saw a name appear nowhere, and gave up. Worse, the dropdown
 * is the control the answer actually comes out of, so on a phone the sequence
 * was: tap the box, type, dismiss the keyboard, tap the picker, scroll.
 *
 * Here the label names the thing being chosen ("Client"), and typing in it
 * narrows the list underneath. One tap, one field, and the name of whoever is
 * chosen stays in the box afterwards.
 *
 * Keyboard and screen-reader behavior follows the APG combobox pattern with a
 * listbox popup (spec §27), the same as the address box: focus never leaves
 * the input, `aria-activedescendant` moves the reading position, and the
 * number of matches is announced rather than only rendered.
 */

export interface SearchSelectOption {
  value: string;
  label: string;
  /** A second line, for telling two similar names apart — an email, an address. */
  description?: string;
}

export interface SearchSelectProps {
  /** Names the thing being chosen, not the act of searching: "Client", not "Find a client". */
  label: string;
  /** The chosen value, or `''` for nothing chosen. */
  value: string;
  /**
   * The chosen value, and the row it came from.
   *
   * The row is handed over because the caller frequently cannot look it up
   * again: the list is about to go back to being unfiltered, and a customer
   * found by searching is often not on that first page. Anyone who needs the
   * name — to store it, to show it somewhere else — has to be given it at the
   * moment of choosing rather than sent back to a list that has moved on.
   */
  onChange: (value: string, option: SearchSelectOption | null) => void;
  /** What can be chosen. Already narrowed by the server when `onSearch` is given. */
  options: ReadonlyArray<SearchSelectOption>;
  /**
   * Stands in for the empty choice: shown in the box when nothing is chosen,
   * and offered as a row to go back to. Reads like "Choose a client".
   */
  placeholder: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /**
   * Called with what has been typed, once typing stops. Given when the list
   * comes from the server; left off when `options` already holds everything,
   * in which case the typing filters them here instead.
   */
  onSearch?: (query: string) => void;
  searchDebounceMs?: number;
  /** Whether a search is in flight, so the list can say so rather than look empty. */
  loading?: boolean;
  /**
   * Whether the lookup itself failed.
   *
   * Kept apart from an empty result deliberately. A failed request leaves the
   * caller with no rows and nothing fetching, which is indistinguishable from
   * a search that matched nobody — and only one of those means "this customer
   * does not exist". Told they have no match, somebody creates the duplicate.
   */
  errored?: boolean;
  /** Passed through to the input, for forms that read values off the DOM. */
  name?: string;
}

const DEFAULT_DEBOUNCE_MS = 250;

export default function SearchSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  hint,
  error,
  required,
  disabled,
  onSearch,
  searchDebounceMs = DEFAULT_DEBOUNCE_MS,
  loading,
  errored,
  name,
}: SearchSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  /**
   * What has been typed, or `null` when the box is showing the choice rather
   * than a search. The two are deliberately different states: an empty search
   * ("show me everyone") has to be tellable from not searching at all, or
   * backspacing the last character would put the chosen name back in the box
   * underneath the cursor.
   */
  const [query, setQuery] = useState<string | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listbox = useRef<HTMLUListElement>(null);

  /**
   * The text the server was last actually asked about.
   *
   * Held because the typing is debounced: for a quarter of a second after a
   * keystroke, and for as long again as the request takes, `options` is still
   * the answer to something ELSE. Without this the control cannot tell which.
   */
  const [dispatched, setDispatched] = useState('');

  const debouncedSearch = useDebouncedCallback((next: string) => {
    setDispatched(next);
    onSearch?.(next);
  }, searchDebounceMs);

  /**
   * The chosen row, remembered.
   *
   * Searching again replaces the options, and the client who was chosen three
   * keystrokes ago is usually not in the new list — the old dropdown dealt with
   * that by looking empty while still holding the id, which is how you send an
   * invoice to somebody whose name is nowhere on screen. Holding on to the row
   * means the box keeps showing who is chosen no matter what the list currently
   * contains.
   */
  const [remembered, setRemembered] = useState<SearchSelectOption | null>(null);
  useEffect(() => {
    const found = options.find((option) => option.value === value);
    if (!found) return;
    setRemembered((current) =>
      current && current.value === found.value && current.label === found.label ? current : found,
    );
  }, [options, value]);

  const chosen =
    options.find((option) => option.value === value) ?? (remembered && remembered.value === value ? remembered : null);

  const typed = query ?? '';
  /** The box is showing a query rather than the choice — even an empty one. */
  const editing = query !== null;
  const searching = editing && typed.trim() !== '';

  /**
   * Whether the rows in hand belong to an older query than the one on screen.
   *
   * This is the difference between a picker and a hazard. The typing is
   * debounced, so for 250ms plus a round trip the list still holds the
   * PREVIOUS search's customers — and the invoice and change-order composers
   * hold theirs right through the request (`keepPreviousData`). Highlighting
   * row 0 of that list, or letting a click land on it, means somebody types a
   * name, presses Enter, and raises a financial record against whoever
   * happened to be first in the answer to a question they have already moved
   * on from.
   *
   * So while the two disagree there are no rows to take, only "Searching…" —
   * the same rule the address box keeps, and it makes "what is listed matches
   * what is typed" true at every moment. Local pickers are never stale: they
   * filter the options they were handed, synchronously.
   *
   * Emptying the box counts. Backspacing a finished search back to nothing is
   * a request for everyone again, and until that request lands the rows in
   * hand are still the narrowed ones — a list that looks complete while most
   * of the customers are missing from it.
   */
  const stale = Boolean(onSearch) && editing && (typed.trim() !== dispatched.trim() || Boolean(loading));

  /**
   * Filtered here only when the caller has not taken the search on itself.
   * Filtering server-narrowed results a second time would hide rows that
   * matched on something the label does not show — an email, a phone number.
   */
  const matches = useMemo(() => {
    const real = options.filter((option) => option.value !== '');
    if (onSearch || !searching) return real;
    const needle = typed.trim().toLowerCase();
    return real.filter((option) => `${option.label} ${option.description ?? ''}`.toLowerCase().includes(needle));
  }, [options, onSearch, searching, typed]);

  /**
   * The rows, with the empty choice first while browsing.
   *
   * It is offered as a way back to "nobody chosen", so it is pointless when
   * that is already the state, and noise in the middle of a search — nobody
   * types three letters to find "Choose a client".
   */
  const rows = useMemo<SearchSelectOption[]>(() => {
    if (stale) return [];
    return !searching && value !== '' ? [{ value: '', label: placeholder }, ...matches] : matches;
  }, [matches, placeholder, searching, stale, value]);

  /*
    Typing highlights the first match, so a name typed in full is taken by
    Enter without an arrow key first — the thing people actually do. Browsing
    with no query highlights nothing, so Enter submits the form as it would
    from any other field.
  */
  useEffect(() => {
    setActiveIndex(searching && rows.length > 0 ? 0 : -1);
  }, [searching, typed, rows.length]);

  /*
    The popup scrolls, so the highlight has to be dragged along with it.
    Without this, half a dozen ArrowDowns move the active row below the fold
    and Enter takes a customer the keyboard user cannot see.
  */
  useEffect(() => {
    if (activeIndex < 0) return;
    const row = listbox.current?.children[activeIndex];
    // Optional-called: jsdom has no layout and does not implement this.
    (row as HTMLElement | undefined)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  /*
    Selecting the text belongs on focus, but taking an option leaves focus
    exactly where it was — so the newly shown name would never get selected,
    and the next keystroke would append to it ("Ridge & Cosun") instead of
    starting a search. Same after Escape puts the name back.
  */
  const reselect = useRef(false);
  useEffect(() => {
    if (!reselect.current) return;
    reselect.current = false;
    // `select()` focuses as well as selects, so it is only right when focus
    // never left. An outside press that settled the search has already moved
    // it — usually onto the button being pressed — and dragging it back would
    // swallow that press.
    if (document.activeElement === input.current) input.current?.select();
  });

  /*
    The pointer press that dismisses the list is frequently the same press that
    submits the form, and `mousedown` runs before `click` — so whatever this
    leaves behind is what the submit sees.

    Restoring the old choice here, which is what Escape does, is therefore the
    one thing it must not do: somebody who chose A, typed B and clicked "Create
    invoice" would get an invoice for A, off a field that said B when they
    clicked it. Enter is already refused mid-search; a mouse must not be the
    way around that.

    So the choice is dropped instead. Nobody has been chosen for what the box
    is showing, so nothing is left behind for that click to submit, and each
    composer's own "nobody chosen, nothing created" guard stops there. The
    typed text stays put, so the field shows why.

    It deliberately does NOT take a query that happens to have narrowed to one
    row. That was tried and it is wrong: a single match is an accident of the
    data rather than a statement of intent, and committing it turns a click on
    an unrelated field into a change of customer. On the Magic Quote screen it
    was worse than that — no customer is a supported answer there, and the
    button says "Start without a customer", so a lone match would have started
    a walkthrough for somebody the button had just promised not to use.

    Held in a ref so the listener is not torn down and rebuilt on every
    keystroke, and so it never settles against a stale list.
  */
  const settle = useRef<() => void>(() => {});
  settle.current = () => {
    setOpen(false);
    setActiveIndex(-1);
    if (!editing || value === '') return;
    onChange('', null);
    setRemembered(null);
  };

  /*
    Listening while the box holds a query even if the list is already closed.
    Closing it is not resolving it — the chevron does exactly that — and a
    listener that stopped there would leave the stale choice behind for the
    next click to submit, which is the whole hazard above with one more step
    in front of it.
  */
  useEffect(() => {
    if (!open && !editing) return;
    const outside = (event: Event) => !wrapper.current?.contains(event.target as Node);

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (outside(event)) settle.current();
    };

    /*
      A press is not the only way a button gets activated. A screen reader's
      double tap and a voice-control "click that" synthesize a bare `click`
      with no `mousedown` in front of it — this control relies on exactly that
      for its own options — so a listener that waits for a press would let an
      assistive-technology user submit the retained customer while the field
      still shows an unresolved search for somebody else. The one hazard this
      component exists to prevent, reachable only by the people least able to
      see it happen.

      Capture phase, so it runs before the button's own handler, and flushed
      synchronously: React would otherwise batch the state update until after
      that handler had already read the old value, which is the whole problem
      restated.
    */
    const onClickCapture = (event: MouseEvent) => {
      if (!editing || !outside(event)) return;
      flushSync(() => settle.current());
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [open, editing]);

  const expanded = open && !disabled;

  function take(option: SearchSelectOption) {
    /*
      Remembered here rather than left to the effect below to notice.

      Taking a row found by searching immediately asks for the unfiltered list
      back, and react-query can answer that from cache synchronously — so the
      searched row can be gone from `options` before the effect ever ran, and
      the effect only ever remembers rows it can still see. The box would go
      blank with the id still set, which is precisely the failure `remembered`
      exists to prevent, reintroduced by the reset that follows.

      The component knows what it just took. It should not have to find out.
    */
    const row = option.value === '' ? null : option;
    setRemembered(row);
    onChange(option.value, row);
    setQuery(null);
    setOpen(false);
    setActiveIndex(-1);
    reselect.current = true;
    // The query that found them has served its purpose, and it is no longer
    // on screen. Left in place, the server's list stays narrowed to it, so
    // reopening the field to change the choice would offer the one customer
    // just picked and look like the whole list.
    resetServerSearch();
  }

  /** Puts the server's list back to unfiltered, if it was ever narrowed. */
  function resetServerSearch() {
    debouncedSearch.cancel();
    if (onSearch && dispatched !== '') {
      setDispatched('');
      onSearch('');
    }
  }

  /** Back to showing the choice, having changed nothing. */
  function abandonSearch() {
    setQuery(null);
    setOpen(false);
    setActiveIndex(-1);
    if (!editing) return;
    reselect.current = true;
    // An abandoned search must not go on to fire, nor leave the server's list
    // narrowed to it.
    resetServerSearch();
  }

  function clear() {
    onChange('', null);
    setQuery(null);
    setRemembered(null);
    setOpen(false);
    setActiveIndex(-1);
    // The list is the server's answer to the last thing typed. Clearing the
    // box is a request for everyone back, not for those matches minus one.
    debouncedSearch.cancel();
    setDispatched('');
    onSearch?.('');
    input.current?.focus();
  }

  return (
    <div className="relative" ref={wrapper}>
      <Field label={label} hint={hint} error={error} required={required}>
        {({ id, describedBy, invalid }) => (
          <>
            <div className="relative">
              <input
                ref={input}
                id={id}
                name={name}
                type="text"
                role="combobox"
                // The browser's own history dropdown would sit on top of this
                // list and offer things that are not choices here.
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-autocomplete="list"
                aria-expanded={expanded}
                aria-controls={`${id}-listbox`}
                aria-activedescendant={expanded && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                // Deliberately not natively `required`: what is in the box is
                // a search, not the answer, so the browser would pass a form
                // with three letters typed and nobody chosen. `required` here
                // marks the label and is enforced where the form is submitted.
                disabled={disabled}
                // Room for the buttons sitting on top of the box, so a long
                // name runs under the label rather than under the chevron.
                className={classes('field-control', value !== '' && !disabled ? 'pr-24' : 'pr-12')}
                placeholder={placeholder}
                value={query ?? chosen?.label ?? ''}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setOpen(true);
                  if (onSearch) debouncedSearch.run(event.target.value);
                }}
                // Opened on the press rather than on focus alone: focus arrives
                // when the previous field is tabbed out of, and a list dropping
                // over the fields below on the way past is not an answer to
                // anything.
                onMouseDown={() => setOpen(true)}
                // Clicking a box that is already focused collapses the
                // selected label to a caret, and the next keystrokes then
                // extend the name instead of replacing it ("Ridgesun"). The
                // press has finished by `click`, so reselecting here sticks.
                // Guarded on not editing: mid-search, a click is somebody
                // placing the caret in their own typing.
                onClick={() => {
                  if (!editing && chosen) input.current?.select();
                }}
                // The box holds the name of whoever is chosen, and the next
                // thing anyone does here is look for somebody else. Selecting
                // it means the first keystroke replaces the name rather than
                // landing on the end of it and searching for "Acme Facilitiesq".
                onFocus={(event) => event.target.select()}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    setOpen(true);
                    if (rows.length > 0) setActiveIndex((current) => (current + 1) % rows.length);
                  } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    setOpen(true);
                    if (rows.length > 0) setActiveIndex((current) => (current <= 0 ? rows.length - 1 : current - 1));
                  } else if (event.key === 'Home' && expanded) {
                    if (rows.length > 0) setActiveIndex(0);
                  } else if (event.key === 'End' && expanded) {
                    if (rows.length > 0) setActiveIndex(rows.length - 1);
                  } else if (event.key === 'Enter') {
                    /*
                      Enter confirms the composition, not the customer, while
                      an IME is open — so a name being typed in Japanese or
                      Korean would otherwise be cut off half-composed and a
                      highlighted row taken in its place.
                    */
                    if (event.nativeEvent.isComposing) return;
                    const row = expanded ? rows[activeIndex] : undefined;
                    /*
                      Swallowed whenever the box is showing a query rather than
                      the choice — whether or not that query landed on a row.
                      Otherwise Enter on a search that matched nobody submits
                      the form around it, and the invoice goes out to whoever
                      was chosen BEFORE, while the field on screen shows the
                      name that was being typed to replace them.

                      With nothing being typed it still submits, which is what
                      Enter does in every other field on the form.
                    */
                    // Swallowed when a row is being taken, as well as
                    // whenever the box is showing a query. Browsing with no
                    // query and arrowing onto a row is the case that slipped
                    // through: Enter meant only "this customer", and without
                    // this it chose them AND created the invoice behind them.
                    if (editing || row) event.preventDefault();
                    if (row) take(row);
                  } else if (event.key === 'Escape') {
                    // Stopped only when there is a list to close, so Escape
                    // still reaches the dialog this may be sitting in.
                    if (expanded) event.stopPropagation();
                    abandonSearch();
                  } else if (event.key === 'Tab') {
                    abandonSearch();
                  }
                }}
              />

              <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-1">
                {value !== '' && !disabled ? (
                  <button
                    type="button"
                    // Full height rather than a 32px square: this is a target
                    // a thumb has to hit on a phone (spec §27).
                    className="flex h-full w-11 items-center justify-center rounded text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]"
                    // A pointer press here must not blur the input first: the
                    // blur would close the list and unmount this button before
                    // its click landed.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clear}
                  >
                    <span className="sr-only">{t('searchSelect.clear')}</span>
                    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" aria-hidden="true">
                      <path d="M5 5l10 10M15 5L5 15" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                ) : null}
                {/*
                  Named and reachable rather than decorative. It is a real way
                  to open the list — the only obvious one for somebody who
                  reads the control as a dropdown — and an unnamed button is
                  nothing at all to a screen reader. Out of the tab order
                  (`tabIndex={-1}`) because the input beside it already answers
                  every key this would, which is what the APG pattern asks for.
                */}
                <button
                  type="button"
                  className="flex h-full w-11 items-center justify-center rounded text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]"
                  tabIndex={-1}
                  aria-label={expanded ? t('searchSelect.hideChoices') : t('searchSelect.showChoices')}
                  aria-expanded={expanded}
                  aria-controls={`${id}-listbox`}
                  disabled={disabled}
                  // Split across the two events on purpose. The press only
                  // keeps focus in the box — the whole control behaves as one,
                  // so the caret stays put and the next keystroke still
                  // searches. The toggle itself is on `click`, which is the
                  // event a screen reader's double tap and a voice-control
                  // "click that" actually send: this button carries a name and
                  // sits outside the tab order, so activating it by click is
                  // an advertised path, and on press alone it did nothing at
                  // all for the people most likely to use it.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    input.current?.focus();
                  }}
                  onClick={() => {
                    input.current?.focus();
                    setOpen((current) => !current);
                  }}
                >
                  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" aria-hidden="true">
                    <path d="M6 8l4 4 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            {expanded ? (
              <ul
                ref={listbox}
                id={`${id}-listbox`}
                role="listbox"
                aria-label={label}
                className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-[color:var(--color-line)] bg-white shadow-lg"
              >
                {rows.map((option, index) => (
                  <li
                    key={`${option.value}-${index}`}
                    id={`${id}-option-${index}`}
                    role="option"
                    aria-selected={option.value === value}
                    // Activated on `click` so the ways a click arrives without
                    // a mouse still work: a screen reader's double tap and a
                    // voice-control "click that" both synthesize a bare click.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => take(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={classes(
                      'cursor-pointer px-3 py-2 text-sm',
                      index === activeIndex ? 'bg-[color:var(--color-brand-soft)]' : '',
                      option.value === '' ? 'text-[color:var(--color-muted)]' : '',
                    )}
                  >
                    <span className={option.value === value ? 'font-semibold' : ''}>{option.label}</span>
                    {option.description ? (
                      <span className="block text-xs text-[color:var(--color-muted)]">{option.description}</span>
                    ) : null}
                  </li>
                ))}

                {rows.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-[color:var(--color-muted)]">
                    {loading || stale
                      ? t('searchSelect.searching')
                      : errored
                        ? t('searchSelect.loadFailed')
                        : t('searchSelect.noMatches')}
                  </li>
                ) : null}
              </ul>
            ) : null}
          </>
        )}
      </Field>

      {/*
        Sighted users watch the list shorten as they type; without this nobody
        else learns that it did, or that a search found nothing (spec §27).
      */}
      <p className="sr-only" role="status">
        {expanded && !loading && !stale ? t('searchSelect.resultsAvailable', { count: rows.length }) : ''}
      </p>
    </div>
  );
}
